import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { GB, PT } from 'country-flag-icons/react/3x2';
import { useI18n } from '../../i18n/I18nContext';
import './LanguageSwitcher.css';

interface LanguageSwitcherProps {
  className?: string;
  minWidth?: number;
}

const languageOptions = [
  { value: 'en' as const, label: 'English', Flag: GB },
  { value: 'pt' as const, label: 'Português', Flag: PT },
] as const;

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className = '', minWidth = 140 }) => {
  const { language, setLanguage, t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement | null>(null);
  const activeLanguage = useMemo(
    () => languageOptions.find((option) => option.value === language) ?? languageOptions[0],
    [language],
  );
  const availableOptions = useMemo(
    () => languageOptions.filter((option) => option.value !== language),
    [language],
  );

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!switcherRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div
      className={`language-switcher ${className}`.trim()}
      aria-label={t('language.label')}
      ref={switcherRef}
      style={{ minWidth }}
    >
      <button
        type="button"
        className={`language-switcher-trigger${menuOpen ? ' open' : ''}`}
        onClick={() => setMenuOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <activeLanguage.Flag className="language-switcher-flag" aria-hidden="true" />
        <span className="language-switcher-label">{activeLanguage.label}</span>
        <FontAwesomeIcon icon={faChevronDown} className="language-switcher-chevron" />
      </button>
      {menuOpen && availableOptions.length > 0 ? (
        <div className="language-switcher-menu" role="menu">
          {availableOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className="language-switcher-option"
              onClick={() => {
                setLanguage(option.value);
                setMenuOpen(false);
              }}
              role="menuitemradio"
              aria-checked={false}
            >
              <option.Flag className="language-switcher-flag" aria-hidden="true" />
              <span className="language-switcher-option-label">{option.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default LanguageSwitcher;
